import React, { useState, useEffect, useMemo } from "react";
import { X, ChevronLeft, ChevronRight, PlaneTakeoff, Clock, Calendar as CalendarIcon, MessageSquare } from "lucide-react";
import AppConst from "../AppConst";
import { useAuth } from "../context/AuthContext";
import { ExperienceDetailsDto } from "../types";
import { BCM_STYLE as S } from "../styles/BookingCalendarStyle";
import { useAppContext } from "../context/AppContext";
import InstrumentLoader from "./InstrumentLoader";
import ConfirmationAlert from "./ConfirmationAlert";
import { message } from "antd";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  registration: string;
  hourlyRate: number;
  durationInMinutes?: number;
  timeInterval?: string;
  experienceId: number;
  experienceDetails?: ExperienceDetailsDto;
}

const ExperienceAvailabilityModal = ({
  isOpen,
  onClose,
  registration,
  hourlyRate,
  durationInMinutes = 60,
  timeInterval = "06:00 - 22:00",
  experienceId,
  experienceDetails
}: Props) => {
  const { requestExperienceBooking } = useAppContext();
  const { currentUser } = useAuth();
  const [viewDate, setViewDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  const [selectedSlot, setSelectedSlot] = useState<any | null>(null);
  const [specialRequirements, setSpecialRequirements] = useState("");
  const [localLoading, setLocalLoading] = useState(false);
  const [isAlertOpen, setIsAlertOpen] = useState(false);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const parsedHours = useMemo(() => {
    try {
      let cleanInterval = timeInterval;
      if (cleanInterval && cleanInterval.includes("—")) {
        cleanInterval = cleanInterval.replace("—", "-");
      }
      const parts = cleanInterval.split("-");
      if (parts.length === 2) {
        const startH = parseInt(parts[0].split(":")[0].trim(), 10);
        const endH = parseInt(parts[1].split(":")[0].trim(), 10);
        if (!isNaN(startH) && !isNaN(endH)) {
          return { startHour: startH, endHour: endH };
        }
      }
    } catch (e) {
      console.error("Eroare la parsarea intervalului orar specific:", e);
    }
    return { startHour: 6, endHour: 22 };
  }, [timeInterval]);

  const SIMPLE_CONFIRMATION_DICT = {
    title: "Validare // Înregistrare Experiență",
    subtitle: "Confirmare Rezervare",
    description: (
      <>
        Ești pe cale să trimiți o cerere de rezervare pentru această experiență aviatică.
        Vei primi o notificare după ce instructorul/operatorul validează intervalul solicitat.
      </>
    ),
    info: [
      "Poți anula această rezervare din coș fără penalizări până la pragul de 72h doar după confirmarea de către operator.",
      "Plata va deveni disponibilă imediat după obținerea aprobării de zbor."
    ],
    btnCancel: "Anulează",
    btnConfirm: "Trimite Cererea"
  };

  const CONFIRMATION_DICT = {
    title: "Validare // Planificare Urgentă",
    subtitle: "Confirmare Rezervare sub 72h",
    description: (
      <>
        Această experiență include un slot orar planificat în următoarele{" "}
        <span className="text-white font-bold underline decoration-orange-500/50">
          72 de ore
        </span>.
      </>
    ),
    info: [
      <>
        Rezervarea <span className="text-red-400 font-bold">nu va mai putea fi anulată</span> odată ce părăsește terminalul de comandă.
      </>,
      <>
        Neplata după confirmare atrage <span className="text-red-400 font-bold">suspendarea contului</span> conform reglementărilor HangarX.
      </>
    ],
    btnCancel: "Anulează",
    btnConfirm: "Trimite Cererea"
  };

  const [activeDict, setActiveDict] = useState(SIMPLE_CONFIRMATION_DICT);
  const content = AppConst.CONTENT;

  // 🟢 EXTRAGEM CORECT SLOTURILE DIN payload-ul venită din backend
  const timeSlotDtoList = experienceDetails?.calendarPayloadDto?.timeSlotDtoList || [];

  const allPossibleSlots = useMemo(() => {
    if (!selectedDate) return [];
    const baseDate = new Date(selectedDate);
    baseDate.setHours(0, 0, 0, 0);
    const slots = [];

    const slotDuration = experienceDetails?.durationInMinutes || durationInMinutes || 60;
    const startMin = parsedHours.startHour * 60;
    const endMin = parsedHours.endHour * 60;
    const totalMinutes = endMin - startMin;

    for (let offset = 0; offset < totalMinutes; offset += slotDuration) {
      const currentTotalMinutes = startMin + offset;
      const h = Math.floor(currentTotalMinutes / 60);
      const m = currentTotalMinutes % 60;

      const sTime = new Date(baseDate).setHours(h, m, 0, 0);
      const eTime = sTime + slotDuration * 60000;

      // Căutăm potrivirea în lista de sloturi primită
      const matchingSlot = timeSlotDtoList.find(slotDto => {
        if (!slotDto.from || !slotDto.to) return false;
        const fYear = slotDto.from.year ?? today.getFullYear();
        const fMonth = (slotDto.from.month ?? 1) - 1;
        const fDay = slotDto.from.day ?? 1;
        const fHour = slotDto.from.hour ?? 0;
        const fMinute = slotDto.from.minute ?? 0;

        const slotStartTime = new Date(fYear, fMonth, fDay, fHour, fMinute, 0, 0).getTime();

        const tYear = slotDto.to.year ?? today.getFullYear();
        const tMonth = (slotDto.to.month ?? 1) - 1;
        const tDay = slotDto.to.day ?? 1;
        const tHour = slotDto.to.hour ?? 0;
        const tMinute = slotDto.to.minute ?? 0;
        const slotEndTime = new Date(tYear, tMonth, tDay, tHour, tMinute, 0, 0).getTime();

        return sTime >= slotStartTime && sTime < slotEndTime;
      });

      const slotTypeEnumMap: Record<number, string> = {
        1: 'FREE_FOR_RENT',
        2: 'BLOCKED_BY_USER'
      };

      const mappedState = matchingSlot ? slotTypeEnumMap[matchingSlot.timeSlotType!] : 'default';

      let computedState = 'default';
      if (mappedState === 'FREE_FOR_RENT') {
        computedState = 'AVAILABLE';
      } else if (mappedState === 'BLOCKED_BY_USER') {
        computedState = 'MAINTENANCE';
      }

      slots.push({
        startTime: sTime,
        endTime: eTime,
        state: computedState,
        label: `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`,
        slotDto: matchingSlot
      });
    }
    return slots;
  }, [selectedDate, timeSlotDtoList, durationInMinutes, experienceDetails?.durationInMinutes, parsedHours]);

  useEffect(() => {
    if (isOpen && timeSlotDtoList.length > 0) {
      const acum = Date.now();

      const viitoareDisponibile = timeSlotDtoList.filter((slotDto) => {
        if (!slotDto.from || slotDto.timeSlotType !== 1) return false;
        const fYear = slotDto.from.year ?? today.getFullYear();
        const fMonth = (slotDto.from.month ?? 1) - 1;
        const fDay = slotDto.from.day ?? 1;
        const fHour = slotDto.from.hour ?? 0;
        const fMinute = slotDto.from.minute ?? 0;
        const slotStartTime = new Date(fYear, fMonth, fDay, fHour, fMinute, 0, 0).getTime();
        return slotStartTime >= acum;
      });

      if (viitoareDisponibile.length > 0) {
        viitoareDisponibile.sort((a, b) => {
          const aTime = new Date(a.from!.year!, (a.from!.month! - 1), a.from!.day!, a.from!.hour!, a.from!.minute!).getTime();
          const bTime = new Date(b.from!.year!, (b.from!.month! - 1), b.from!.day!, b.from!.hour!, b.from!.minute!).getTime();
          return aTime - bTime;
        });

        const f = viitoareDisponibile[0].from!;
        const celMaiApropiatSlot = new Date(f.year!, (f.month! - 1), f.day!, f.hour!, f.minute!);
        setViewDate(celMaiApropiatSlot);
      } else {
        setViewDate(new Date());
      }
    }
  }, [isOpen, timeSlotDtoList]);

  const availableDatesSet = useMemo(() => {
    const set = new Set<string>();
    if (!timeSlotDtoList) return set;

    timeSlotDtoList.forEach((slotDto) => {
      if (slotDto.timeSlotType === 1 && slotDto.from) {
        const fYear = slotDto.from.year ?? today.getFullYear();
        const fMonth = (slotDto.from.month ?? 1) - 1;
        const fDay = slotDto.from.day ?? 1;
        const dataCheie = new Date(fYear, fMonth, fDay).toDateString();
        set.add(dataCheie);
      }
    });
    return set;
  }, [timeSlotDtoList]);

  const toggleSlot = (slotObj: any) => {
    setSelectedSlot(prev => prev?.startTime === slotObj.startTime ? null : slotObj);
  };

  const startTimeLabel = useMemo(() => {
    if (!selectedSlot) return null;
    return new Date(selectedSlot.startTime).toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' });
  }, [selectedSlot]);

  const handleRequest = () => {
    if (!selectedDate || !selectedSlot) return;

    const now = Date.now();
    const seventyTwoHoursInMs = 72 * 60 * 60 * 1000;
    const isWithin72Hours = (selectedSlot.startTime - now) < seventyTwoHoursInMs;

    if (isWithin72Hours) {
      setActiveDict(CONFIRMATION_DICT);
    } else {
      setActiveDict(SIMPLE_CONFIRMATION_DICT);
    }
    setIsAlertOpen(true);
  };

  const executeBooking = async () => {
      if (!selectedSlot) return;
      setIsAlertOpen(false);
      setLocalLoading(true);
      try {
        let timeSlotToSubmit = selectedSlot.slotDto;
        if (!timeSlotToSubmit) {
          const startDate = new Date(selectedSlot.startTime);
          const endDate = new Date(selectedSlot.endTime);
          timeSlotToSubmit = {
            uuid: crypto.randomUUID ? crypto.randomUUID() : String(Math.random()),
            bookingId: 0,
            timeSlotType: 1,
            from: {
              timePointType: 1,
              year: startDate.getFullYear(),
              month: startDate.getMonth() + 1,
              day: startDate.getDate(),
              hour: startDate.getHours(),
              minute: startDate.getMinutes()
            },
            to: {
              timePointType: 2,
              year: endDate.getFullYear(),
              month: endDate.getMonth() + 1,
              day: endDate.getDate(),
              hour: endDate.getHours(),
              minute: endDate.getMinutes()
            }
          };
        }
        const experienceBookingDto = {
          user: currentUser ? {
            cnxObjId: currentUser.cnxObjId,
            userName: currentUser.userName,
            email: currentUser.email,
            firstName: currentUser.firstName,
            lastName: currentUser.lastName
          } : null,
          experience: { experienceId: experienceId },
          timeSlotDto: timeSlotToSubmit,
          additionalDetails: specialRequirements,
          estimatedPrice: hourlyRate,
          status: "PENDING",
          paymentStatus: "UNPAID",
          createdAt: Date.now()
        };
        const success = await requestExperienceBooking(experienceBookingDto);

        if (success) {
          message.success({ content: "Cererea de experiență a fost înregistrată!", duration: AppConst.msgDuration });
          setSelectedSlot(null);
          setSpecialRequirements("");
          onClose();
        } else {
          message.error({ content: "Eroare la trimiterea cererii...", duration: AppConst.msgDuration });
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLocalLoading(false);
      }
    };

  useEffect(() => {
    if (!isOpen) { setSelectedDate(null); setSelectedSlot(null); setSpecialRequirements(""); }
  }, [isOpen]);

  if (!isOpen) return null;

  const currentMonth = viewDate.getMonth();
  const currentYear = viewDate.getFullYear();
  const monthNames = ["IAN", "FEB", "MAR", "APR", "MAI", "IUN", "IUL", "AUG", "SEP", "OCT", "NOI", "DEC"];
  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  const adjustedFirstDay = new Date(currentYear, currentMonth, 1).getDay() === 0 ? 6 : new Date(currentYear, currentMonth, 1).getDay() - 1;

  const currentDuration = experienceDetails?.durationInMinutes || durationInMinutes || 60;

  return (
      <div className={S.overlay}>
        <InstrumentLoader visible={localLoading} />
        <div className="z-[110] relative">
          <ConfirmationAlert
            isOpen={isAlertOpen}
            onClose={() => setIsAlertOpen(false)}
            onConfirm={executeBooking}
            dict={activeDict}
          />
        </div>
        <div className={`${S.modal} max-h-[90vh] overflow-hidden`}>

          <div className={S.sidebarLeft}>
            <div className={S.stepHeader}>
              <div className={S.stepIconBlue}><CalendarIcon size={20} /></div>
              <div>
                <p className={S.stepLabel}>Pasul 01</p>
                <h3 className={S.stepTitle}>Alege Data</h3>
              </div>
            </div>

            <div className={S.calendarNav}>
              <button
                onClick={() => setViewDate(new Date(currentYear, currentMonth - 1, 1))}
                className={S.calendarNavBtn}
                disabled={currentYear <= today.getFullYear() && currentMonth <= today.getMonth()}
              >
                <ChevronLeft size={18} style={{ opacity: (currentYear <= today.getFullYear() && currentMonth <= today.getMonth()) ? 0.3 : 1 }}/>
              </button>
              <span className={S.monthDisplay}>{monthNames[currentMonth]} {currentYear}</span>
              <button onClick={() => setViewDate(new Date(currentYear, currentMonth + 1, 1))} className={S.calendarNavBtn}><ChevronRight size={18}/></button>
            </div>

            <div className={S.calendarGrid}>
              {['L', 'M', 'M', 'J', 'V', 'S', 'D'].map(h => <div key={h} className={S.weekdayLabel}>{h}</div>)}
              {Array.from({ length: adjustedFirstDay }).map((_, idx) => <div key={`spacer-${idx}`} />)}
              {Array.from({ length: daysInMonth }, (_, i) => {
                const d = new Date(currentYear, currentMonth, i + 1);
                const dStr = d.toDateString();
                const isSelected = selectedDate?.getDate() === (i + 1) && selectedDate?.getMonth() === currentMonth;
                const isPast = d < today;

                const hasNoSlots = !availableDatesSet.has(dStr);
                const isDayDisabled = isPast || hasNoSlots;

                return (
                  <button
                    key={i}
                    disabled={isDayDisabled}
                    onClick={() => {
                      if (!isDayDisabled) {
                        setSelectedDate(d);
                        setSelectedSlot(null);
                      }
                    }}
                    className={`
                      ${S.dayButtonBase}
                      ${isSelected ? S.dayButtonActive : S.dayButtonInactive}
                      ${isDayDisabled ? 'opacity-20 cursor-not-allowed grayscale bg-slate-950/10' : ''}
                    `}
                  >
                    {i + 1}
                  </button>
                );
              })}
            </div>
          </div>

          <div className={`${S.mainContent} flex flex-col h-full overflow-hidden`}>
            <div className="space-y-6 flex-1 flex flex-col overflow-hidden min-h-0">
              <section className={`${!selectedDate ? S.sectionDisabled : S.sectionActive} flex flex-col min-h-0 flex-1`}>
                <div className={`${S.timelineSection} shrink-0`}>
                  <div className="flex items-center gap-4">
                    <div className="relative">
                      <div className={S.clockGlow}></div>
                      <div className={S.clockIconBox}>
                        <Clock size={20} />
                      </div>
                    </div>
                    <div>
                      <p className={S.stepLabel}>Pasul 02</p>
                      <h3 className={S.timelineHeaderTitle}>
                        Intervale Disponibile <span className={S.utcTag}>RO_TIME</span>
                      </h3>
                    </div>
                  </div>

                  <div className={S.timelineLegendBox}>
                    {S.legendItems.map((item) => (
                      <div key={item.label} className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${item.color}`}></div>
                        <span className={S.legendText}>{item.label}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto pr-1 no-scrollbar pt-2 min-h-0">
                  <div className="flex items-center gap-2 mb-3 opacity-40">
                    <span className="text-[9px] font-mono font-black uppercase tracking-wider bg-slate-950 px-2 py-0.5 rounded border border-white/5">
                      Fereastră Orare: {experienceDetails?.timeInterval || timeInterval}
                    </span>
                    <div className={S.rowDivider}></div>
                  </div>

                  <div className={S.slotsGrid}>
                    {allPossibleSlots.map((slot) => {
                      const isAvailable = slot.state === 'AVAILABLE';
                      const isMaintenance = slot.state === 'MAINTENANCE';
                      const isSelected = selectedSlot?.startTime === slot.startTime;

                      let stateKey: keyof typeof S.slotStates = 'default';
                      if (isSelected) stateKey = 'selected';
                      else if (isMaintenance) stateKey = 'maintenance';
                      else if (isAvailable) stateKey = 'available';

                      return (
                        <button
                          key={slot.startTime}
                          disabled={!isAvailable}
                          onClick={() => toggleSlot(slot)}
                          className={`${S.slotBase} ${S.slotStates[stateKey]}`}
                        >
                          <span className={`text-[10px] font-mono font-bold ${isSelected ? S.slotTextActive : S.slotTextInactive}`}>
                            {slot.label}
                          </span>
                          <span className={`${S.slotStatusLabel} ${isSelected ? S.slotStatusActive : S.slotStatusInactive}`}>
                            {isSelected ? 'ACTIVE' : isMaintenance ? 'BLOCKED' : isAvailable ? 'OPEN' : 'N/A'}
                          </span>
                          {isAvailable && !isSelected && <div className={S.slotDot}></div>}
                          {isSelected && (
                            <div className={S.slotPing}>
                              <span className={S.pingContainer}>
                                <span className={S.pingAnimate}></span>
                                <span className={S.pingInner}></span>
                              </span>
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </section>

              <section className={`${S.missionSection} ${!selectedSlot ? S.sectionDisabled : S.sectionActive} shrink-0`}>
                <div className="flex items-center gap-3 mb-3">
                  <div className={S.iconBoxPurple}><MessageSquare size={20} /></div>
                  <div>
                    <p className={S.stepLabel}>Pasul 03</p>
                    <h3 className={S.stepTitle}>Observații sau Cerințe Speciale <span className="text-slate-600 text-[10px] lowercase font-normal">(Opțional)</span></h3>
                  </div>
                </div>

                <div className="w-full">
                  <textarea
                    rows={2}
                    disabled={!selectedSlot}
                    placeholder="Ex: Preferințe instructor, echipament foto adițional, observații medicale sau organizatorice..."
                    value={specialRequirements}
                    onChange={(e) => setSpecialRequirements(e.target.value)}
                    className="w-full bg-[#1E293B]/40 border border-[#334155]/60 rounded-2xl p-3 text-xs text-white placeholder:text-slate-600 outline-none focus:border-orange-500/50 focus:ring-1 focus:ring-orange-500/30 transition-all resize-none disabled:opacity-30 disabled:cursor-not-allowed"
                  />
                </div>
              </section>
            </div>
          </div>

          <div className={S.sidebarRight}>
            <button onClick={onClose} className={S.closeBtn}><X size={20} /></button>

            <div className="flex-1 space-y-8">
              <div>
                 <p className={S.unitLabel}>EXPERIENȚĂ SELECTATĂ</p>
                 <h4 className="text-xs font-black text-white font-sans uppercase tracking-tight italic line-clamp-2 max-w-[200px] leading-tight">
                   {registration}
                 </h4>
              </div>

              <div className={S.detailsCard}>
                <div className={`${S.detailRow} ${S.rowBorderB}`}>
                  <span className={S.detailLabel}>{content.date}</span>
                  <span className={S.detailValueDefault}>{selectedDate?.toLocaleDateString('ro-RO') || '---'}</span>
                </div>

                <div className={S.detailRow}>
                  <span className={S.detailLabel}>Ora start</span>
                  <span className={S.detailValueTime}>{startTimeLabel || '--:--'}</span>
                </div>

                <div className={`${S.detailRow} ${S.rowBorderT}`}>
                  <span className={S.detailLabel}>Durată Slot</span>
                  <span className={S.detailValueDefault}>
                    {selectedSlot ? `${currentDuration} min` : '---'}
                  </span>
                </div>
              </div>

              <div className={S.costCard}>
                 <p className={S.costLabel}>Tarif Fix Pachet</p>
                 <div className="flex items-baseline gap-1">
                   <span className={S.priceValue}>
                     €{selectedSlot ? hourlyRate.toFixed(2) : "0.00"}
                   </span>
                   <span className={S.currencyLabel}>{content.currency}</span>
                 </div>
              </div>
            </div>

            <button
              type="button"
              onClick={handleRequest}
              disabled={!selectedSlot}
              className={`${S.confirmBtnBase} ${selectedSlot ? S.confirmBtnActive : S.confirmBtnDisabled}`}
            >
              {localLoading ? 'SE PROCESEAZĂ...' : 'Rezervă Experiență'} <PlaneTakeoff size={18} />
            </button>
          </div>
        </div>
      </div>
  );
};

export default ExperienceAvailabilityModal;